import { Suspense, lazy, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Spin } from 'antd';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { checkAuth, setCheckingAuth } from '@/store/slices/authSlice';

// Import layout directly (not lazy) so it loads with the app
import DashboardLayout from './layouts/DashboardLayout';

// Lazy load pages
const LoginPage = lazy(() => import('./pages/auth/LoginPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const NotFoundPage = lazy(() => import('./pages/errors/NotFoundPage'));

// User pages
const UserListPage = lazy(() => import('./pages/users/UserListPage'));
const UserDetailPage = lazy(() => import('./pages/users/UserDetailPage'));
const UserFormPage = lazy(() => import('./pages/users/UserFormPage'));
const CashbackListPage = lazy(() => import('./pages/users/CashbackListPage'));
const InactiveUsersPage = lazy(() => import('./pages/users/InactiveUsersPage'));
const DeletedUsersPage = lazy(() => import('./pages/users/DeletedUsersPage'));

// Doctor pages
const DoctorListPage = lazy(() => import('./pages/doctors/DoctorListPage'));
const DoctorDetailPage = lazy(() => import('./pages/doctors/DoctorDetailPage'));
const DoctorFormPage = lazy(() => import('./pages/doctors/DoctorFormPage'));
const DoctorEditPage = lazy(() => import('./pages/doctors/DoctorEditPage'));

// Doctor Master Data pages
const SpecialityListPage = lazy(() => import('./pages/doctors/masterData/SpecialityListPage'));
const TreatmentListPage = lazy(() => import('./pages/doctors/masterData/TreatmentListPage'));
const QualificationListPage = lazy(() => import('./pages/doctors/masterData/QualificationListPage'));
const SchoolListPage = lazy(() => import('./pages/doctors/masterData/SchoolListPage'));
const TemplateListPage = lazy(() => import('./pages/doctors/masterData/TemplateListPage'));
const CptCodeListPage = lazy(() => import('./pages/doctors/masterData/CptCodeListPage'));
const DayTimeListPage = lazy(() => import('./pages/doctors/masterData/DayTimeListPage'));
const DurationListPage = lazy(() => import('./pages/doctors/masterData/DurationListPage'));
const UnitListPage = lazy(() => import('./pages/doctors/masterData/UnitListPage'));

// Administrator pages
const ProfilePage = lazy(() => import('./pages/administrators/ProfilePage'));
const AdminListPage = lazy(() => import('./pages/administrators/AdminListPage'));
const RolesPermissionsPage = lazy(() => import('./pages/administrators/RolesPermissionsPage'));

// Settings pages
const GeneralSettingsPage = lazy(() => import('./pages/settings/GeneralSettingsPage'));
const PaymentSettingsPage = lazy(() => import('./pages/settings/PaymentSettingsPage'));
const AgoraSettingsPage = lazy(() => import('./pages/settings/AgoraSettingsPage'));
const SmsSettingsPage = lazy(() => import('./pages/settings/SmsSettingsPage'));
const EmailSettingsPage = lazy(() => import('./pages/settings/EmailSettingsPage'));
// Appointment pages
const AppointmentListPage = lazy(() => import('./pages/appointments/AppointmentListPage'));
const AppointmentDetailPage = lazy(() => import('./pages/appointments/AppointmentDetailPage'));
const AppointmentFormPage = lazy(() => import('./pages/appointments/AppointmentFormPage'));
const WaitingRoomPage = lazy(() => import('./pages/appointments/WaitingRoomPage'));
const AppointmentNotesPage = lazy(() => import('./pages/appointments/AppointmentNotesPage'));

// Prescription pages
const PrescriptionListPage = lazy(() => import('./pages/prescriptions/PrescriptionListPage'));
const PrescriptionDetailPage = lazy(() => import('./pages/prescriptions/PrescriptionDetailPage'));
const PrescriptionFormPage = lazy(() => import('./pages/prescriptions/PrescriptionFormPage'));

// Clinic pages
const ClinicListPage = lazy(() => import('./pages/clinics/ClinicListPage'));
const ClinicDetailPage = lazy(() => import('./pages/clinics/ClinicDetailPage'));
const ClinicFormPage = lazy(() => import('./pages/clinics/ClinicFormPage'));
const ClinicDoctorsPage = lazy(() => import('./pages/clinics/ClinicDoctorsPage'));
const ClinicDoctorsListPage = lazy(() => import('./pages/clinics/ClinicDoctorsListPage'));
const ClinicAppointmentsPage = lazy(() => import('./pages/clinics/ClinicAppointmentsPage'));

// Clinic Billing pages
const ClinicSubscriptionPage = lazy(() => import('./pages/clinics/billing/ClinicSubscriptionPage'));
const ClinicInvoicesPage = lazy(() => import('./pages/clinics/billing/ClinicInvoicesPage'));
const ClinicBillingHistoryPage = lazy(() => import('./pages/clinics/billing/ClinicBillingHistoryPage'));
const ClinicCreditsPage = lazy(() => import('./pages/clinics/billing/ClinicCreditsPage'));

// Billing pages (admin)
const BillingDashboardPage = lazy(() => import('./pages/billing/BillingDashboardPage'));
const AllInvoicesPage = lazy(() => import('./pages/billing/AllInvoicesPage'));
const InvoiceDetailPage = lazy(() => import('./pages/billing/InvoiceDetailPage'));
const SubscriptionPlansPage = lazy(() => import('./pages/billing/SubscriptionPlansPage'));
const PromoCodesPage = lazy(() => import('./pages/billing/PromoCodesPage'));

// Notification pages
const NotificationListPage = lazy(() => import('./pages/notifications/NotificationListPage'));
const NotificationDetailPage = lazy(() => import('./pages/notifications/NotificationDetailPage'));
const SendNotificationPage = lazy(() => import('./pages/notifications/SendNotificationPage'));
const NotificationTemplatesPage = lazy(() => import('./pages/notifications/NotificationTemplatesPage'));
const NotificationTemplateFormPage = lazy(() => import('./pages/notifications/NotificationTemplateFormPage'));
const NotificationStatsPage = lazy(() => import('./pages/notifications/NotificationStatsPage'));

// Agent pages
const AgentListPage = lazy(() => import('./pages/agents/AgentListPage'));
const AgentDetailPage = lazy(() => import('./pages/agents/AgentDetailPage'));
const AgentFormPage = lazy(() => import('./pages/agents/AgentFormPage'));
const PayoutListPage = lazy(() => import('./pages/agents/PayoutListPage'));

// Master Data pages
const EmailTemplateListPage = lazy(() => import('./pages/master-data/email-templates/EmailTemplateListPage'));
const EmailTemplateFormPage = lazy(() => import('./pages/master-data/email-templates/EmailTemplateFormPage'));
const SmsTemplateListPage = lazy(() => import('./pages/master-data/sms-templates/SmsTemplateListPage'));
const SmsTemplateFormPage = lazy(() => import('./pages/master-data/sms-templates/SmsTemplateFormPage'));
const PageListPage = lazy(() => import('./pages/master-data/pages/PageListPage'));
const PageFormPage = lazy(() => import('./pages/master-data/pages/PageFormPage'));
const BlogListPage = lazy(() => import('./pages/master-data/blogs/BlogListPage'));
const BlogFormPage = lazy(() => import('./pages/master-data/blogs/BlogFormPage'));
const StateListPage = lazy(() => import('./pages/master-data/states/StateListPage'));
const StateFormPage = lazy(() => import('./pages/master-data/states/StateFormPage'));
const BannerListPage = lazy(() => import('./pages/master-data/banners/BannerListPage'));
const BannerFormPage = lazy(() => import('./pages/master-data/banners/BannerFormPage'));
const BlockedListPage = lazy(() => import('./pages/master-data/blocked/BlockedListPage'));
const InsuranceProviderListPage = lazy(() => import('./pages/master-data/insurance-providers/InsuranceProviderListPage'));
const InsuranceProviderFormPage = lazy(() => import('./pages/master-data/insurance-providers/InsuranceProviderFormPage'));
const ConditionListPage = lazy(() => import('./pages/master-data/conditions/ConditionListPage'));
const ConditionFormPage = lazy(() => import('./pages/master-data/conditions/ConditionFormPage'));

// Support pages
const TicketListPage = lazy(() => import('./pages/support/tickets/TicketListPage'));
const TicketDetailPage = lazy(() => import('./pages/support/tickets/TicketDetailPage'));
const TicketFormPage = lazy(() => import('./pages/support/tickets/TicketFormPage'));

// Reports pages
const ReportsPage = lazy(() => import('./pages/reports/ReportsPage'));
const AppointmentsReportPage = lazy(() => import('./pages/reports/AppointmentsReportPage'));
const RevenueReportPage = lazy(() => import('./pages/reports/RevenueReportPage'));
const DoctorsReportPage = lazy(() => import('./pages/reports/DoctorsReportPage'));
const UsersReportPage = lazy(() => import('./pages/reports/UsersReportPage'));
const PrescriptionsReportPage = lazy(() => import('./pages/reports/PrescriptionsReportPage'));

// Loading fallback
const PageLoader = () => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      flexDirection: 'column',
      gap: 16,
    }}
  >
    <Spin size="large" />
    <span style={{ color: '#8c8c8c' }}>Loading...</span>
  </div>
);

// Private Route Component
const PrivateRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, checkingAuth } = useAppSelector((state) => state.auth);

  if (checkingAuth) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Public Route Component (redirect to dashboard if authenticated)
const PublicRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, checkingAuth } = useAppSelector((state) => state.auth);

  if (checkingAuth) {
    return <PageLoader />;
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
};

function App() {
  const dispatch = useAppDispatch();

  // Check auth ONLY on initial mount — not when token changes.
  // After loginAsync.fulfilled, Redux state is already correct; no re-check needed.
  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('accessToken');
    if (token) {
      dispatch(checkAuth()).then(() => {
        if (cancelled) return; // StrictMode double-mount guard
        // If checkAuth failed and we don't have auth, checkingAuth is already set to false by the reducer
      });
    } else {
      dispatch(setCheckingAuth(false));
    }
    return () => { cancelled = true; };
  }, [dispatch]);

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Auth Routes (Public) */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPasswordPage />
            </PublicRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PublicRoute>
              <ResetPasswordPage />
            </PublicRoute>
          }
        />

        {/* Dashboard Routes (Private) */}
        <Route
          path="/"
          element={
            <PrivateRoute>
              <DashboardLayout />
            </PrivateRoute>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />

          {/* Settings */}
          <Route path="settings">
            <Route index element={<Navigate to="/settings/general" replace />} />
            <Route path="general" element={<GeneralSettingsPage />} />
            <Route path="payment" element={<PaymentSettingsPage />} />
            <Route path="agora" element={<AgoraSettingsPage />} />
            <Route path="sms" element={<SmsSettingsPage />} />
            <Route path="email" element={<EmailSettingsPage />} />
          </Route>

          {/* Administrators */}
          <Route path="administrators">
            <Route index element={<Navigate to="/administrators/profile" replace />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="list" element={<AdminListPage />} />
            <Route path="roles" element={<RolesPermissionsPage />} />
          </Route>

          {/* Users (Patients) */}
          <Route path="users">
            <Route index element={<UserListPage />} />
            {/* Specific routes MUST come before :id to avoid matching issues */}
            <Route path="create" element={<UserFormPage />} />
            <Route path="cashbacks" element={<CashbackListPage />} />
            <Route path="inactive" element={<InactiveUsersPage />} />
            <Route path="deleted" element={<DeletedUsersPage />} />
            {/* Parameterized routes come last */}
            <Route path=":id" element={<UserDetailPage />} />
            <Route path=":id/edit" element={<UserFormPage />} />
          </Route>

          {/* Doctors */}
          <Route path="doctors">
            <Route index element={<DoctorListPage />} />
            {/* Specific routes MUST come before :id to avoid matching issues */}
            <Route path="create" element={<DoctorEditPage />} />
            <Route path="master/specialities" element={<SpecialityListPage />} />
            <Route path="master/treatments" element={<TreatmentListPage />} />
            <Route path="master/qualifications" element={<QualificationListPage />} />
            <Route path="master/schools" element={<SchoolListPage />} />
            <Route path="master/templates" element={<TemplateListPage />} />
            <Route path="master/cpt-codes" element={<CptCodeListPage />} />
            <Route path="master/day-times" element={<DayTimeListPage />} />
            <Route path="master/durations" element={<DurationListPage />} />
            <Route path="master/units" element={<UnitListPage />} />
            {/* Parameterized routes come last */}
            <Route path=":id" element={<DoctorDetailPage />} />
            <Route path=":id/edit" element={<DoctorEditPage />} />
            <Route path=":id/quick-edit" element={<DoctorFormPage />} />
          </Route>

          {/* Appointments */}
          <Route path="appointments">
            <Route index element={<AppointmentListPage />} />
            {/* Specific routes MUST come before :id to avoid matching issues */}
            <Route path="create" element={<AppointmentFormPage />} />
            <Route path="waiting-room" element={<WaitingRoomPage />} />
            <Route path="upcoming" element={<AppointmentListPage />} />
            <Route path="past" element={<AppointmentListPage />} />
            <Route path="completed" element={<AppointmentListPage />} />
            <Route path="cancelled" element={<AppointmentListPage />} />
            <Route path="unfinished" element={<AppointmentListPage />} />
            <Route path="notes/unfinished" element={<AppointmentListPage />} />
            <Route path="notes/finished" element={<AppointmentListPage />} />
            <Route path="claims/*" element={<div>Claims</div>} />
            {/* Parameterized routes come last */}
            <Route path=":id" element={<AppointmentDetailPage />} />
            <Route path=":id/edit" element={<AppointmentFormPage />} />
            <Route path=":id/notes" element={<AppointmentNotesPage />} />
          </Route>

          {/* Prescriptions */}
          <Route path="prescriptions">
            <Route index element={<PrescriptionListPage />} />
            {/* Specific routes MUST come before :id to avoid matching issues */}
            <Route path="create" element={<PrescriptionFormPage />} />
            {/* Parameterized routes come last */}
            <Route path=":id" element={<PrescriptionDetailPage />} />
            <Route path=":id/edit" element={<PrescriptionFormPage />} />
          </Route>

          {/* Clinics */}
          <Route path="clinics">
            <Route index element={<ClinicListPage />} />
            {/* Specific routes MUST come before :id to avoid matching issues */}
            <Route path="list" element={<ClinicListPage />} />
            <Route path="create" element={<ClinicFormPage />} />
            <Route path="doctors" element={<ClinicDoctorsListPage />} />
            <Route path="appointments" element={<ClinicAppointmentsPage />} />
            {/* Parameterized routes come last */}
            <Route path=":clinicId" element={<ClinicDetailPage />} />
            <Route path=":clinicId/edit" element={<ClinicFormPage />} />
            <Route path=":clinicId/doctors" element={<ClinicDoctorsPage />} />
            {/* Clinic Billing routes */}
            <Route path=":clinicId/subscription" element={<ClinicSubscriptionPage />} />
            <Route path=":clinicId/invoices" element={<ClinicInvoicesPage />} />
            <Route path=":clinicId/payments" element={<ClinicBillingHistoryPage />} />
            <Route path=":clinicId/credits" element={<ClinicCreditsPage />} />
          </Route>

          {/* Billing (Admin) */}
          <Route path="billing">
            <Route index element={<BillingDashboardPage />} />
            <Route path="dashboard" element={<BillingDashboardPage />} />
            <Route path="invoices" element={<AllInvoicesPage />} />
            <Route path="invoices/:id" element={<InvoiceDetailPage />} />
            <Route path="plans" element={<SubscriptionPlansPage />} />
            <Route path="promo-codes" element={<PromoCodesPage />} />
          </Route>

          {/* Notifications */}
          <Route path="notifications">
            <Route index element={<NotificationListPage />} />
            <Route path="send" element={<SendNotificationPage />} />
            <Route path="stats" element={<NotificationStatsPage />} />
            <Route path="templates" element={<NotificationTemplatesPage />} />
            <Route path="templates/create" element={<NotificationTemplateFormPage />} />
            <Route path="templates/:id/edit" element={<NotificationTemplateFormPage />} />
            <Route path=":id" element={<NotificationDetailPage />} />
          </Route>

          {/* Coupons */}
          <Route path="coupons/*" element={<div>Coupons</div>} />

          {/* Agents */}
          <Route path="agents">
            <Route index element={<AgentListPage />} />
            <Route path="create" element={<AgentFormPage />} />
            <Route path="payouts" element={<PayoutListPage />} />
            <Route path=":id" element={<AgentDetailPage />} />
            <Route path=":id/edit" element={<AgentFormPage />} />
          </Route>

          {/* FTC */}
          <Route path="ftc/*" element={<div>Fast Track Consult</div>} />

          {/* Master Data */}
          <Route path="master-data">
            <Route index element={<Navigate to="/master-data/email-templates" replace />} />
            {/* Email Templates */}
            <Route path="email-templates" element={<EmailTemplateListPage />} />
            <Route path="email-templates/create" element={<EmailTemplateFormPage />} />
            <Route path="email-templates/:id/edit" element={<EmailTemplateFormPage />} />
            {/* SMS Templates */}
            <Route path="sms-templates" element={<SmsTemplateListPage />} />
            <Route path="sms-templates/create" element={<SmsTemplateFormPage />} />
            <Route path="sms-templates/:id/edit" element={<SmsTemplateFormPage />} />
            {/* Pages (CMS) */}
            <Route path="pages" element={<PageListPage />} />
            <Route path="pages/create" element={<PageFormPage />} />
            <Route path="pages/:id/edit" element={<PageFormPage />} />
            {/* Blogs */}
            <Route path="blogs" element={<BlogListPage />} />
            <Route path="blogs/create" element={<BlogFormPage />} />
            <Route path="blogs/:id/edit" element={<BlogFormPage />} />
            {/* States */}
            <Route path="states" element={<StateListPage />} />
            <Route path="states/create" element={<StateFormPage />} />
            <Route path="states/:id/edit" element={<StateFormPage />} />
            {/* Banners */}
            <Route path="banners" element={<BannerListPage />} />
            <Route path="banners/create" element={<BannerFormPage />} />
            <Route path="banners/:id/edit" element={<BannerFormPage />} />
            {/* Blocked List */}
            <Route path="blocked" element={<BlockedListPage />} />
            {/* Insurance Providers */}
            <Route path="insurance-providers" element={<InsuranceProviderListPage />} />
            <Route path="insurance-providers/create" element={<InsuranceProviderFormPage />} />
            <Route path="insurance-providers/:id/edit" element={<InsuranceProviderFormPage />} />
            {/* Conditions */}
            <Route path="conditions" element={<ConditionListPage />} />
            <Route path="conditions/create" element={<ConditionFormPage />} />
            <Route path="conditions/:id/edit" element={<ConditionFormPage />} />
          </Route>

          {/* Support */}
          <Route path="support">
            <Route index element={<Navigate to="/support/tickets" replace />} />
            <Route path="tickets" element={<TicketListPage />} />
            <Route path="tickets/create" element={<TicketFormPage />} />
            <Route path="tickets/:id" element={<TicketDetailPage />} />
          </Route>

          {/* Reports */}
          <Route path="reports">
            <Route index element={<ReportsPage />} />
            <Route path="appointments" element={<AppointmentsReportPage />} />
            <Route path="revenue" element={<RevenueReportPage />} />
            <Route path="doctors" element={<DoctorsReportPage />} />
            <Route path="users" element={<UsersReportPage />} />
            <Route path="prescriptions" element={<PrescriptionsReportPage />} />
          </Route>
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

export default App;
